/**
 * Pure rules the summary layout shares between the page (SummaryLayout.tsx)
 * and the Apple TV app (appletv/src/components/SummaryLayout.tsx), so node
 * tests cover both. 2026-09-30.
 */
import type {
  TvHudTone,
  TvSummary,
  TvSummaryGridSection,
  TvSummaryMatchBoardSection,
  TvSummarySection,
  TvSummaryText,
} from './protocol';
import { boardRowsForDisplay } from './hudModel';

/** The bottom prompt on every summary; a card that advances by itself shows
 *  it beside the countdown ring (Cole, 2026-09-30: the Ladder and Start Line
 *  run cards count down to the next run, and the phone still owns STOP and
 *  CHANGE DISTANCE). */
export const SUMMARY_CONTINUE_PROMPT = 'Continue on your device.';

/**
 * Text in a column of playing cards centers under them (the Poker result's
 * AVG LEAVE line ran into the left edge while the hands sat centered; Cole,
 * 2026-09-30: "bad centering"). Elsewhere the phone's alignment stands.
 */
export function centeredInColumn(section: TvSummarySection, column: readonly TvSummarySection[]): TvSummarySection {
  if (section.kind !== 'text' || section.align !== 'left') return section;
  return column.some((other) => other.kind === 'hand') ? { ...section, align: 'center' } : section;
}

/** The legend hint as a sentence under the plot. Older phones send the
 *  terse "NUMBER = HOLE". */
export function legendHintText(hint: string | null | undefined): string | null {
  const text = hint?.trim();
  if (!text) return null;
  return /^numbers? = holes?$/i.test(text) ? 'Numbers are hole numbers' : text;
}

/** Most score rows a board draws (Sim 9 local stroke play seats six). */
export const MATCH_BOARD_MAX_PLAYERS = 6;

/** Score rows on a match board: one per name the phone sends (one for a
 *  solo round, two for a match, up to six for stroke play). An older phone
 *  that sent no names still gets its two rows. */
export function matchBoardPlayerCount(section: Pick<TvSummaryMatchBoardSection, 'names'>): number {
  const names = section.names?.length ?? 0;
  if (names <= 0) return 2;
  return Math.min(names, MATCH_BOARD_MAX_PLAYERS);
}

/** Each player's strokes per hole: A, B, then `strokesMore` player by player. */
export function matchBoardStrokeRows(
  section: Pick<TvSummaryMatchBoardSection, 'names' | 'holes' | 'strokesA' | 'strokesB' | 'strokesMore'>,
): Array<Array<number | null>> {
  const holes = section.holes?.length ?? 0;
  const count = matchBoardPlayerCount(section);
  const row = (source: ReadonlyArray<number | null> | null | undefined, offset = 0) =>
    Array.from({ length: holes }, (_, i) => {
      const value = source?.[offset + i];
      return typeof value === 'number' && Number.isFinite(value) ? value : null;
    });
  const rows = [row(section.strokesA), row(section.strokesB)];
  for (let player = 2; player < count; player += 1) rows.push(row(section.strokesMore, (player - 2) * holes));
  return rows.slice(0, count);
}

/** Standings with this many rows draw compact (six stroke-play players under
 *  a six-row scorecard); the page's CSS keys the same count with :has(). */
export const COMPACT_ENTRIES_FROM = 5;
/** A plot legend with this many names draws smaller. */
export const COMPACT_LEGEND_FROM = 4;

/** A grid section's cells split into rows of `columns`. */
export function gridRows(section: Pick<TvSummaryGridSection, 'columns' | 'cells'>): Array<TvSummaryGridSection['cells']> {
  const columns = Math.max(1, Math.floor(section.columns || 1));
  const cells = section.cells ?? [];
  const rows: Array<TvSummaryGridSection['cells']> = [];
  for (let i = 0; i < cells.length; i += columns) rows.push(cells.slice(i, i + columns));
  return rows;
}

/* ---------------------------------------------------------------------------
 * Legacy cards (2026-09-30). A summary without `sections` (a leaderboard, the
 * Profile hub, an older phone's round card) used to draw on a separate card
 * renderer with no prompt and no overscan margins. It now converts to
 * sections here and draws on the same layout as every other summary.
 * ------------------------------------------------------------------------ */

const TONE_COLOR: Record<TvHudTone, string> = {
  gold: '#d4a442',
  success: '#4fd08a',
  danger: '#ef6a6a',
  neutral: '#ffffff',
};
const MUTED = '#829ca8';
const SECONDARY = '#b0bec5';
const GOLD = '#d4a442';

/** True when a footer only repeats the layout's own prompt. */
function repeatsPrompt(text: string): boolean {
  return /\b(choose|continue) on your (device|phone)\b/i.test(text);
}

/** The summary as the shared layout draws it: its own sections, or the
 *  legacy fields (kicker, title, headline, rows, board, cards, hands, footer)
 *  turned into sections. */
export function summaryAsLayout(summary: TvSummary): TvSummary {
  if (Array.isArray(summary.sections) && summary.sections.some((section) => section && typeof section === 'object' && 'kind' in section)) {
    return summary;
  }
  const board = summary.board?.rows?.length ? summary.board : null;
  const hero: TvSummaryText[] = [
    ...(summary.kicker ? [{ text: summary.kicker, color: MUTED, size: 'caption' as const, weight: 'bold' as const, spacing: 0.15 }] : []),
    ...(summary.title ? [{ text: summary.title, size: 'title' as const, weight: 'heavy' as const }] : []),
    ...(summary.headline ? [{ text: summary.headline, color: GOLD, size: 'value' as const, weight: 'heavy' as const }] : []),
    ...(summary.headlineDetail ? [{ text: summary.headlineDetail, color: SECONDARY, size: 'body' as const, weight: 'bold' as const }] : []),
  ];
  const sections: TvSummarySection[] = [{ kind: 'text', column: 0, align: 'center', lines: hero }];
  for (const hand of summary.hands ?? []) {
    sections.push({
      kind: 'hand',
      column: 0,
      cards: hand.cards ?? [],
      lines: [{ text: hand.label, color: TONE_COLOR[hand.tone ?? 'neutral'], size: 'body', weight: 'heavy' }],
      after: hand.detail ? [{ text: hand.detail, color: SECONDARY, size: 'caption', weight: 'bold' }] : null,
    });
  }
  if (summary.cards?.length) sections.push({ kind: 'hand', column: 0, cards: summary.cards });
  const rows = (summary.rows ?? []).slice(0, 8);
  if (rows.length) {
    sections.push({
      kind: 'metrics',
      column: 0,
      perRow: rows.length <= 4 ? rows.length : Math.ceil(rows.length / 2),
      tiles: rows.map((row) => ({
        label: row.label,
        value: row.value,
        labelColor: MUTED,
        valueColor: TONE_COLOR[row.tone ?? 'neutral'],
      })),
    });
  }
  const footer = summary.footer?.trim();
  if (footer && !repeatsPrompt(footer)) {
    sections.push({ kind: 'text', column: 0, align: 'center', lines: [{ text: footer, color: SECONDARY, size: 'body', weight: 'bold' }] });
  }
  if (board) {
    sections.push({
      kind: 'entries',
      column: 1,
      title: board.title,
      rows: boardRowsForDisplay(board.rows).map((row) => ({
        rank: String(row.rank),
        rankColor: MUTED,
        name: row.name,
        nameColor: row.you ? GOLD : '#ffffff',
        value: row.value,
        valueColor: row.you ? GOLD : '#ffffff',
        border: row.you ? GOLD : null,
      })),
    });
  }
  return { ...summary, sections, columns: board ? [1, 1.1] : [1] };
}

/**
 * The start-line chart has seven columns, and its labels (MAJOR, SLIGHT) are
 * one word each. At the full label size they are wider than a column once the
 * chart is narrow: two charts side by side with a plot ran the words together
 * ("Words look a little squashed together on TV", a tester, 2026-10-01). The
 * label size is capped at this fraction of the chart's inner width, which
 * keeps the longest word inside its column. The page applies it as a CSS
 * container unit (summaryLayout.css, 3cqw); Apple TV measures the chart.
 */
export const CHART_LABEL_WIDTH_FRACTION = 0.03;

export function chartLabelFontSize(chartWidth: number, baseSize: number): number {
  if (!(chartWidth > 0)) return baseSize;
  return Math.min(baseSize, chartWidth * CHART_LABEL_WIDTH_FRACTION);
}

/**
 * How much a column of fixed-height sections is scaled to fit the space it
 * has (2026-10-01: six Sim 9 standings rows ran over the scorecard above and
 * the prompt below). 1 until both are measured and whenever it already fits.
 */
export function columnFitScale(availableHeight: number, contentHeight: number): number {
  if (!(availableHeight > 0) || !(contentHeight > 0) || contentHeight <= availableHeight) return 1;
  return availableHeight / contentHeight;
}
